import { SkeletonTiles } from '../Skeleton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

/**
 * Media panel (35h) — parity with Sparked v2's course-editor Media tab. Tabs for Images / Videos / Audios;
 * under Images you generate with NanoBanana or upload; every tile is draggable onto an <img>/<video> in the
 * running preview to swap it (the drop is handled by the in-bundle media-drop script + PreviewPane).
 */

export type MediaKind = 'image' | 'video' | 'audio';
export interface MediaItem {
  id: string;
  kind: MediaKind;
  name: string;
  url: string;
  source: 'generated' | 'uploaded';
  createdAt: string;
}

const TABS: Array<{ kind: MediaKind; label: string; accept: string }> = [
  { kind: 'image', label: 'Images', accept: 'image/*' },
  { kind: 'video', label: 'Videos', accept: 'video/*' },
  { kind: 'audio', label: 'Audios', accept: 'audio/*' },
];
/** style presets with the curated NanoBanana owl previews (ported from Sparked's editor studio, 35i #1) */
const STYLES = [
  { key: 'school', label: 'School default', img: '/art-styles/school.jpg' },
  { key: 'flat', label: 'Flat vector', img: '/art-styles/flat.jpg' },
  { key: '3d', label: '3D clay', img: '/art-styles/3d.jpg' },
  { key: 'watercolor', label: 'Watercolor', img: '/art-styles/watercolor.jpg' },
  { key: 'line', label: 'Line art', img: '/art-styles/line.jpg' },
  { key: 'photo', label: 'Photographic', img: '/art-styles/photo.jpg' },
  { key: 'isometric', label: 'Isometric', img: '/art-styles/isometric.jpg' },
  { key: 'comic', label: 'Comic', img: '/art-styles/comic.jpg' },
];
const ASPECTS = ['16:9', '1:1', '4:5', '9:16', '4:3'];
/** aspect → preview box dims (w×h in px, ≤26) so the dropdown shows the shape, like Sparked */
const ASPECT_DIMS: Record<string, { w: number; h: number }> = {
  '16:9': { w: 26, h: 15 },
  '1:1': { w: 20, h: 20 },
  '4:5': { w: 16, h: 20 },
  '9:16': { w: 12, h: 22 },
  '4:3': { w: 24, h: 18 },
};

function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.8 4.9L18.7 9.7 13.8 11.5 12 16.4 10.2 11.5 5.3 9.7 10.2 7.9zM19 15l.9 2.4L22.3 18.3 19.9 19.2 19 21.6 18.1 19.2 15.7 18.3 18.1 17.4z" />
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 15V4M8 8l4-4 4 4M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
function FilmIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" />
    </svg>
  );
}
function MusicIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l10-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="16" cy="16" r="3" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });

interface MediaPanelProps {
  projectId: string;
  onDragMedia: (item: { kind: MediaKind; url: string } | null) => void;
  onSaved: () => void;
}

export function MediaPanel({ projectId, onDragMedia, onSaved }: MediaPanelProps) {
  const [tab, setTab] = useState<MediaKind>('image');
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('school');
  const [aspect, setAspect] = useState('16:9');
  const [transparent, setTransparent] = useState(false);
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const [aspectOpen, setAspectOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ items: MediaItem[] }>(`/box/projects/${projectId}/media/library`);
      setItems(data.items);
    } catch {
      setStatus('Could not load your media');
      setItems([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    setStatus(null);
    try {
      const { item } = await api.post<{ item: MediaItem }>(`/box/projects/${projectId}/media/generate`, {
        prompt: prompt.trim(),
        style,
        aspect,
        transparent,
      });
      setItems(prev => [item, ...(prev ?? [])]);
      setPrompt('');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not generate that image');
    } finally {
      setGenerating(false);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 9 * 1024 * 1024) {
      setStatus('That file is too big — 9MB is the limit here');
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const dataUrl = await readDataUrl(file);
      const { item } = await api.post<{ item: MediaItem }>(`/box/projects/${projectId}/media/upload`, {
        kind: tab,
        name: file.name,
        dataUrl,
      });
      setItems(prev => [item, ...(prev ?? [])]);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not upload that file');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    setItems(prev => (prev ?? []).filter(i => i.id !== id));
    try {
      await api.delete(`/box/projects/${projectId}/media/library/${id}`);
    } catch {
      void load(); // put it back if the delete failed
    }
  };

  const shown = (items ?? []).filter(i => i.kind === tab);
  const accept = TABS.find(t => t.kind === tab)!.accept;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <input ref={fileRef} type="file" accept={accept} onChange={onFile} className="hidden" />

      {/* art-style preview panel (35i #1) — a grid of style previews, Sparked-style */}
      {stylePickerOpen && (
        <div className="absolute inset-0 z-30 flex flex-col bg-surface">
          <div className="flex shrink-0 items-center justify-between border-b border-line px-3 py-2.5">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-ink">Art style</p>
            <button type="button" onClick={() => setStylePickerOpen(false)} aria-label="Close" className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 overflow-auto p-3">
            {STYLES.map(s => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  setStyle(s.key);
                  setStylePickerOpen(false);
                }}
                className={[
                  'group overflow-hidden rounded-xl border-2 text-left transition',
                  s.key === style ? 'border-accent ring-2 ring-accent/30' : 'border-line hover:border-accent/50',
                ].join(' ')}
              >
                <img src={s.img} alt={s.label} className="block aspect-square w-full bg-page object-cover" />
                <span className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-ink">
                  {s.label}
                  {s.key === style && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent-deep">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="flex shrink-0 gap-1 border-b border-line px-3 pt-2">
        {TABS.map(t => (
          <button
            key={t.kind}
            type="button"
            onClick={() => setTab(t.kind)}
            className={[
              '-mb-px border-b-2 px-2.5 py-1.5 text-xs font-semibold transition',
              tab === t.kind ? 'border-accent text-accent-deep' : 'border-transparent text-muted hover:text-ink',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {/* generate — images only */}
        {tab === 'image' && (
          <div className="mb-3 rounded-2xl border border-line bg-page/50 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-accent-deep">
              <SparkleIcon /> Generate an image
            </p>
            <textarea
              rows={2}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Describe the image…"
              className="mb-2 block w-full resize-none rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent/60"
            />
            <div className="mb-2 grid grid-cols-2 gap-2">
              {/* Art style — opens a panel of style previews (Sparked-style) */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Art style</span>
                <button
                  type="button"
                  onClick={() => setStylePickerOpen(true)}
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface px-1.5 text-xs transition hover:border-accent/50"
                >
                  <img src={STYLES.find(s => s.key === style)?.img} alt="" className="h-5 w-5 shrink-0 rounded object-cover" />
                  <span className="min-w-0 flex-1 truncate text-left">{STYLES.find(s => s.key === style)?.label}</span>
                  <Chevron />
                </button>
              </div>
              {/* Aspect — custom dropdown with shape previews */}
              <div className="relative flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Aspect</span>
                <button
                  type="button"
                  onClick={() => setAspectOpen(o => !o)}
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface px-2 text-xs transition hover:border-accent/50"
                >
                  <span className="grid h-5 w-5 shrink-0 place-items-center">
                    <span className="rounded-[2px] border-2 border-current text-muted" style={{ width: ASPECT_DIMS[aspect].w / 2 + 4, height: ASPECT_DIMS[aspect].h / 2 + 3 }} />
                  </span>
                  <span className="min-w-0 flex-1 text-left tabular-nums">{aspect}</span>
                  <Chevron />
                </button>
                {aspectOpen && (
                  <>
                    <button type="button" aria-label="Close" className="fixed inset-0 z-10 cursor-default" onClick={() => setAspectOpen(false)} />
                    <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-xl border border-line bg-surface p-1 shadow-xl">
                      {ASPECTS.map(a => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => {
                            setAspect(a);
                            setAspectOpen(false);
                          }}
                          className={['flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition hover:bg-page', a === aspect ? 'text-accent-deep' : 'text-ink'].join(' ')}
                        >
                          <span className="grid h-6 w-6 shrink-0 place-items-center">
                            <span className="rounded-[2px] border-2 border-current" style={{ width: ASPECT_DIMS[a].w, height: ASPECT_DIMS[a].h }} />
                          </span>
                          <span className="tabular-nums">{a}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTransparent(v => !v)}
              className="mb-2 flex w-full items-center justify-between rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs transition hover:border-accent/50"
            >
              <span>Transparent background</span>
              <span className={['relative h-4 w-7 rounded-full transition', transparent ? 'bg-accent' : 'bg-line'].join(' ')}>
                <span className={['absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', transparent ? 'left-3.5' : 'left-0.5'].join(' ')} />
              </span>
            </button>
            <button
              type="button"
              onClick={() => void generate()}
              disabled={!prompt.trim() || generating}
              className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-bold text-accent-ink transition hover:opacity-90 disabled:opacity-50"
            >
              {generating ? 'Generating…' : 'Generate with NanoBanana'}
            </button>
          </div>
        )}

        {/* upload */}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line py-3 text-sm font-semibold text-muted transition hover:border-accent/50 hover:text-ink disabled:opacity-50"
        >
          <UploadIcon />
          {uploading ? 'Uploading…' : `Upload ${TABS.find(t => t.kind === tab)!.label.toLowerCase()}`}
        </button>

        {/* grid */}
        {items === null ? (
          <div aria-busy="true" aria-label="Loading media">
            <SkeletonTiles count={4} className="grid-cols-2 sm:grid-cols-2" />
          </div>
        ) : shown.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted">
            {tab === 'image' ? 'Generate or upload an image to get started.' : `Upload a ${tab} to get started.`}
          </p>
        ) : (
          <>
            <p className="mb-2 px-0.5 text-[11px] text-muted">Drag a tile onto a matching element in your app to swap it.</p>
            <div className="grid grid-cols-2 gap-2">
              {shown.map(item => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={e => {
                    e.dataTransfer.effectAllowed = 'copy';
                    e.dataTransfer.setData('text/plain', item.url);
                    onDragMedia({ kind: item.kind, url: item.url });
                  }}
                  onDragEnd={() => {
                    onDragMedia(null);
                    onSaved();
                  }}
                  className="group relative cursor-grab overflow-hidden rounded-lg border border-line bg-page active:cursor-grabbing"
                  title={`${item.name} — drag onto your app`}
                >
                  {item.kind === 'image' ? (
                    <img src={item.url} alt={item.name} draggable={false} className="aspect-square w-full object-cover" />
                  ) : (
                    <div className="grid aspect-square place-items-center text-muted">{item.kind === 'video' ? <FilmIcon /> : <MusicIcon />}</div>
                  )}
                  {item.source === 'generated' && (
                    <span className="absolute left-1.5 top-1.5 rounded bg-accent px-1.5 py-0.5 text-[9px] font-bold text-accent-ink">AI</span>
                  )}
                  <button
                    type="button"
                    onClick={() => void remove(item.id)}
                    aria-label="Remove from library"
                    className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-ink/70 text-[11px] text-white opacity-0 transition group-hover:opacity-100 hover:bg-danger"
                  >
                    ×
                  </button>
                  <span className="block truncate border-t border-line bg-surface px-1.5 py-1 text-[10px] text-muted">{item.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {status && <p className="shrink-0 border-t border-line px-3 py-2 text-xs text-muted">{status}</p>}
    </div>
  );
}
